import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { DataValidationDemandChart } from '@/modules/forecast-review/components/DataValidationDemandChart';
import type { DataValidationSelectedProduct } from '@/modules/forecast-review/components/DataValidationProductsTable';
import type { ForecastReviewDataValidationProductSeriesResponse } from '@/services/forecastReviewService';
import { formatUiDate } from '@/utils/uiLocale';

interface DataValidationProductPanelProps {
  product: DataValidationSelectedProduct;
  warehouseLabel?: string | null;
  series: ForecastReviewDataValidationProductSeriesResponse | null;
  loading: boolean;
  error: boolean;
  onClose: () => void;
}

function formatDateLabel(isoDate: string, language?: string | null): string {
  return formatUiDate(`${isoDate}T12:00:00`, { day: '2-digit', month: 'short', year: 'numeric' }, language);
}

function pickStockoutBannerSpan(
  spans: ForecastReviewDataValidationProductSeriesResponse['stockout_spans']
) {
  if (!spans.length) return null;
  return [...spans].sort((a, b) => b.end_date.localeCompare(a.end_date))[0];
}

export function DataValidationProductPanel({
  product,
  warehouseLabel,
  series,
  loading,
  error,
  onClose,
}: DataValidationProductPanelProps) {
  const { t, i18n } = useTranslation();

  const productLabel = [product.product_name, product.sku].filter(Boolean).join(' · ') || '—';
  const stockoutSpan = pickStockoutBannerSpan(series?.stockout_spans ?? []);

  const gapChips = useMemo(() => {
    if (!series?.gap_periods.length) return [];
    return series.gap_periods.map((date) => ({
      key: date,
      label: formatDateLabel(date, i18n.language),
    }));
  }, [series?.gap_periods]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">{productLabel}</h4>
          <p className="mt-0.5 text-xs text-gray-500">
            {warehouseLabel
              ? t('forecastModule.dataValidation.panel.warehouse', { name: warehouseLabel })
              : null}
            {series?.cycle_label ? (
              <>
                {warehouseLabel ? ' · ' : ''}
                {t('forecastModule.dataValidation.cycleLabel', { cycle: series.cycle_label })}
              </>
            ) : null}
          </p>
          {series?.horizon_start && series.horizon_end ? (
            <p className="mt-0.5 text-xs text-gray-400">
              {t('forecastModule.dataValidation.panel.horizon', {
                start: formatDateLabel(series.horizon_start, i18n.language),
                end: formatDateLabel(series.horizon_end, i18n.language),
              })}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label={t('forecastModule.dataValidation.panel.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {stockoutSpan ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {t('forecastModule.dataValidation.panel.stockoutSpan', {
            start: formatDateLabel(stockoutSpan.start_date, i18n.language),
            weeks: stockoutSpan.week_count,
          })}
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-6 text-center text-sm text-red-700">
          {t('forecastModule.dataValidation.panel.loadError')}
        </div>
      ) : (
        <DataValidationDemandChart data={series} loading={loading} />
      )}

      {gapChips.length > 0 ? (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="mb-2 text-xs font-medium text-gray-600">
            {t('forecastModule.dataValidation.panel.gapChipsTitle', { count: gapChips.length })}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {gapChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-700"
              >
                {chip.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {series?.summary && !loading && !error ? (
        <p className="mt-3 text-xs text-gray-500">
          {t('forecastModule.dataValidation.panel.summary', {
            stockouts: series.summary.stockout_count,
            outliers: series.summary.outlier_count,
            gaps: series.summary.gap_count,
          })}
        </p>
      ) : null}
    </div>
  );
}
